import Header from '@/components/UI/Header';
import { _isNotEmpty, _notNil } from '@/littledash';
import type { SseStreamStatus } from '@/support/Hooks/sse/useSseStream';
import { usePresenceStream } from '@/support/Hooks/sse/usePresenceStream';
import { type FC } from 'react';

const statusDotColor: Record<SseStreamStatus, string> = {
  connected: 'bg-green',
  connecting: 'bg-yellow',
  disconnected: 'bg-red',
};

const statusLabel: Record<SseStreamStatus, string> = {
  connected: 'Connected',
  connecting: 'Connecting',
  disconnected: 'Disconnected',
};

const emptyMessage: Record<SseStreamStatus, string> = {
  connected: 'No users online',
  connecting: 'Connecting...',
  disconnected: 'Disconnected',
};

/**
 * Admin console page demonstrating real-time SSE presence tracking.
 * Shows which users are currently connected via the messaging service.
 */
export const SsePresence: FC = () => {
  const { status, isConnected, onlineUsers, lastTimestamp, connect, disconnect } = usePresenceStream();

  return (
    <div className="pb3" data-testid="sse-presence">
      <Header mainHeaderText="SSE Presence" />
      <div className="flex flex-row pt4">
        <div className="ui-card pa4 flex flex-column w-50">
          <div className="flex items-center mb3">
            <div
              className={`br-100 ${statusDotColor[status]}`}
              style={{ width: 12, height: 12 }}
              data-testid="sse-presence__status-dot"
              role="status"
              aria-label={statusLabel[status]}
            />
            <span className="ml2 f5 fw5" data-testid="sse-presence__status-text">
              {statusLabel[status]}
            </span>
          </div>

          <div className="mb3">
            <button
              className="btn"
              onClick={isConnected ? disconnect : connect}
              disabled={status === 'connecting'}
              data-testid="sse-presence__toggle-button"
            >
              {isConnected ? 'Disconnect' : 'Connect'}
            </button>
          </div>

          {_notNil(lastTimestamp) && (
            <div className="mb3 f6 gray" data-testid="sse-presence__last-event">
              Last event: {new Date(lastTimestamp).toLocaleTimeString()}
            </div>
          )}

          <div className="mb2 fw5">Users Online ({onlineUsers.length})</div>
          {_isNotEmpty(onlineUsers) ? (
            <ul className="list pl0 ma0" data-testid="sse-presence__user-list">
              {onlineUsers.map((userId) => (
                <li key={userId} className="pv1 bb b--light-gray f6" data-testid="sse-presence__user-item">
                  User {userId}
                </li>
              ))}
            </ul>
          ) : (
            <div className="f6 gray" data-testid="sse-presence__empty">
              {emptyMessage[status]}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
